 <template>
  <div class="modal-content">
    <!-- Title -->
    <h2 class="modal-title">{{ $t('notifications.title') }}</h2>

    <!-- Tabs -->
    <div class="tabs">
      <button
        :class="{ active: activeTab === 'system' }"
        @click="activeTab = 'system'"
      >
        {{ $t('notifications.systemTab') }}
      </button>
      <button
        :class="{ active: activeTab === 'personal' }"
        @click="activeTab = 'personal'"
      >
        {{ $t('notifications.personalTab') }}
      </button>
    </div>

    <!-- Loading -->
    <p v-if="loading" class="loading-message">
      {{ $t('notifications.loading') }}
    </p>

    <!-- Error -->
    <p v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </p>

    <!-- Notification List -->
    <ul v-if="!loading && paginatedNotifications.length" class="notification-list">
      <li
        v-for="notification in paginatedNotifications"
        :key="notification._id"
        class="notification-item"
      >
        <!-- Left -->
        <div class="notification-left">
          <img
            v-if="notification.image"
            :src="getImageUrl(notification.image)"
            class="notification-image"
          />
          <div v-else class="notification-icon">🔔</div>
        </div>

        <!-- Body -->
        <div class="notification-body">
          <h3 class="notification-title">
            {{ notification.title }}
          </h3>

          <p
            class="notification-message"
            :class="{ expanded: expandedIds.includes(notification._id) }"
          >
            {{ notification.message }}
          </p>

          <button
            v-if="notification.message.length > 120"
            class="show-more"
            @click="toggleExpand(notification._id)"
          >
            {{
              expandedIds.includes(notification._id)
                ? $t('notifications.showLess')
                : $t('notifications.showMore')
            }}
          </button>

          <span class="notification-date">
            {{ formatDate(notification.createdAt) }}
          </span>
        </div>
      </li>
    </ul>

    <!-- Empty -->
    <p v-else-if="!loading" class="no-notifications">
      {{ $t('notifications.noNotifications') }}
    </p>

    <!-- Pagination -->
    <div v-if="!loading && totalPages > 1" class="pagination">
      <button :disabled="currentPage === 1" @click="currentPage--">
        {{ $t('notifications.prev') }}
      </button>
      <span>{{ currentPage }} / {{ totalPages }}</span>
      <button :disabled="currentPage === totalPages" @click="currentPage++">
        {{ $t('notifications.next') }}
      </button>
    </div>

  
  </div>
</template>


<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "ModalNotifications",
  data() {
    return {
      notifications: [],
      loading: false,
      errorMessage: "",
      activeTab: "system",
      expandedIds: [],
      currentPage: 1,
      perPage: 5
    };
  },
  computed: {
    ...mapGetters(["authUser"]),
    filteredNotifications() {
      if (this.activeTab === "system") {
        return this.notifications.filter(n => !n.recipientId);
      } else {
        return this.notifications.filter(
          n => n.recipientId === this.authUser?.user?._id
        );
      }
    },
    totalPages() {
      return Math.ceil(this.filteredNotifications.length / this.perPage);
    },
    paginatedNotifications() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredNotifications.slice(start, start + this.perPage);
    }
  },
  methods: {
    async fetchNotifications() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const res = await axios.get(`/notices/${this.authUser.user._id}`);
        this.notifications = res.data;
      } catch (err) {
        console.error(err);
        this.errorMessage = this.$t("notifications.error");
      } finally {
        this.loading = false;
      }
    },
    getImageUrl(path) {
      if (!path) return "";
      const base = process.env.VUE_APP_BACKEND_URL;
      if (path.startsWith("http")) return path;
      return `${base}${path}`;
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    },
    truncateText(text, length) {
      return text.length > length ? text.substring(0, length) + "..." : text;
    },
    toggleExpand(id) {
      if (this.expandedIds.includes(id)) {
        this.expandedIds = this.expandedIds.filter(x => x !== id);
      } else {
        this.expandedIds.push(id);
      }
    },
    markAllAsRead() {
      this.notifications = this.notifications.map(n => ({
        ...n,
        read: true
      }));
    }
  },
  mounted() {
    if (this.authUser?.user?._id) {
      this.fetchNotifications();
    }
  }
};
</script>

<style scoped>
/* Modal */
.modal-content {
  width: 600px;
  max-height: 80vh;
  padding: 30px;
 
  background: var(--background1);
  overflow-y: auto;
  box-shadow: 0 10px 45px rgba(0, 0, 0, 0.35);
}

/* Title */
.modal-title {
  text-align: center;
  font-size: 24px;
  font-weight: 900;
  color: #fff;
  margin-bottom: 22px;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 22px;
}

.tabs button {
  flex: 1;
  padding: 10px;
  border-radius: 10px;
  background: var(--background3);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.3s;
}

.tabs button.active {
  background: var(--buttonyellow);
  box-shadow: var(--buttonyellowbox);
}

/* List */
.notification-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Item */
.notification-item {
  display: flex;
  gap: 14px;
 
  border-radius: 14px;
  background: var(--background2);
  margin-bottom: 14px;
}

/* Left */
.notification-left {
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.notification-image {
  width: 100px;
  height: 100px;
  border-radius: 12px;
 
}

.notification-icon {
  width: 100px;
  height: 100px;
  border-radius: 12px;
  background: var(--background4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

/* Body */
.notification-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: var(--card1-bg);
    padding: 10px;
    border-radius: 10px;
      min-width: 0;
}

/* Title */
.notification-title {
  font-size: 15px;
  font-weight: 800;
  color: #fff;
}

/* Message */
.notification-message {
  font-size: 12px;
  color: #ddd;
  line-height: 1.45;

  word-break: break-word;
  overflow-wrap: anywhere;

  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-message.expanded {
  display: block;              /* ✅ olmazsa olmaz */
  -webkit-line-clamp: unset;
  overflow: visible;
  white-space: normal;
}



/* Show more */
.show-more {
  background: transparent;
  border: none;
  color: var(--buttonyellow);
  font-size: 13px;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
  align-self: flex-start;
}

/* Date */
.notification-date {
  margin-top: 4px;
  font-size: 11px;
  color: #888;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.pagination button {
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--background2);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.pagination button:hover:not(:disabled) {
  background: var(--buttonyellow);
  box-shadow: var(--buttonyellowbox);
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mark read */
.mark-read {
  margin-top: 22px;
}

.mark-read button {
  width: 100%;
  height: 48px;
  border-radius: 10px;
  background: var(--buttonyellow);
  box-shadow: var(--buttonyellowbox);
  color: #fff;
  font-weight: 700;
  cursor: pointer;
}

/* Mobile */
@media (max-width: 560px) {
  .modal-content {
    width: calc(100vw - 0px);
    padding: 20px;
  }

  .notification-left,
  .notification-image,
  .notification-icon {
    width: 64px;
    height: 64px;
  }

  .modal-title {
    font-size: 18px;
  }
}
</style>

 
